import React, { useEffect, useState } from "react";
import axios from "../api/axios";
import { useAuth } from "../context/AuthProvider";
import { Link } from "react-router-dom";
import "./styles/MyOrders.css"; // ✅ Add this for the new styles
import config from "../config/config";
import SEO from "../seo/SEO";

const MyOrders = () => {
  const { auth } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get("/api/user/my-orders");
        setOrders(response.data.orders);
        setLoading(false);
      } catch (error) {
        console.error("🔥 Error fetching orders:", error);
        setError("Failed to load orders.");
        setLoading(false);
      }
    };
  
    if (auth.isAuthenticated) {
      fetchOrders();
    }
  }, [auth.isAuthenticated]);
  
  

  if (loading) {
    return (
      <div className="orders-page-wrapper">
        <div className="orders-container">
          <h2>My Orders</h2>
          <div className="spinner"></div>
          <p>Loading your orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-page-wrapper">
        <div className="orders-container">
          <p className="error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
        <SEO
            robots="noindex, nofollow"
            title="My Orders - Withrow Knives"
            description="View and manage your past orders from Withrow Knives. Check order status, details, and total pricing."
            keywords={[
                "order history",
                "past orders",
                "Withrow Knives orders",
                "order tracking",
                "my orders",
                "knife orders"
            ]}
            url={`${config.metadata.siteUrl}/user/orders`}
            jsonLd={{
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": "My Orders - Withrow Knives",
                "url": `${config.metadata.siteUrl}/user/orders`,
                "description": "View and manage your past orders from Withrow Knives. Check order status, details, and total pricing.",
                "breadcrumb": {
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Home",
                            "item": config.metadata.siteUrl
                        },
                        {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "User",
                            "item": `${config.metadata.siteUrl}/user`
                        },
                        {
                            "@type": "ListItem",
                            "position": 3,
                            "name": "My Orders",
                            "item": `${config.metadata.siteUrl}/user/orders`
                        }
                    ]
                },
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": `${config.metadata.siteUrl}/user/orders`
                }
            }}
        />


    
      <div className="orders-page-wrapper">
        <div className="orders-container">
          <h2>My Orders</h2>

          {orders.length === 0 ? (
            <p aria-live="polite">You have no past orders.</p>
          ) : (
            <div className="orders-list">
              {orders.map((order) => (
              <div key={order.order_uuid} className="order-card">
                <h3>Order Summary</h3>
              
                <p><strong>Order ID:</strong> <span aria-label="Order ID">{order.order_uuid}</span></p>
              
                <p>
                  <strong>Status: </strong> 
                  <span aria-live="polite">
                    {order.status.toLowerCase() === "shipped"
                      ? "Shipped; On its way!"
                      : order.status.toLowerCase() === "cancelled"
                      ? "Order Cancelled"
                      : order.status.toLowerCase() === "complete"
                      ? "Order Complete"
                      : ["paid", "pending"].includes(order.status.toLowerCase())
                      ? "Processing"
                      : order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                </p>
              
                <p><strong>Date Placed: </strong>
                  <span aria-live="polite">
                    {new Date(order.created_at).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long', // "March"
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true, // 12-hour format with AM/PM
                      timeZoneName: 'short' // Shows timezone (e.g., "EST")
                    })}
                  </span>
                </p>
              
                <p><strong>Total:</strong> <span aria-label="Total order price">${Number(order.total_price).toFixed(2)}</span></p>
              
                {order.tracking_number && order.carrier && (
                  <p>
                    <strong>Tracking:</strong>{" "}
                    <a
                      href={
                        order.carrier === "USPS"
                          ? `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${order.tracking_number}`
                          : order.carrier === "UPS"
                          ? `https://www.ups.com/track?tracknum=${order.tracking_number}`
                          : order.carrier === "FEDEX"
                          ? `https://www.fedex.com/fedextrack/?trknbr=${order.tracking_number}`
                          : "/"
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Track your package with ${order.carrier}, tracking number ${order.tracking_number}`}
                    >
                      Track Your Package ({order.carrier})
                    </a>
                  </p>
                )}


              
                <Link to={`/order-details/${order.order_uuid}`} className="view-order-btn" aria-label={`View details for order ${order.order_uuid}`}>
                  View Order <i className="fa-solid fa-arrow-right"></i>
                </Link>
              </div>
              
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MyOrders;
